import React, { useCallback, useRef, useState } from 'react';
import AiEmailExchange from './AIEmailExchange';
import AiCallTranscript from './AiTranscript';
import { useToken } from '@/common/helper';
import {
  oneTimeResolve,
  oneTimeDismiss,
  ResolveAlertComments,
  ResolveAlertCommentsModel,
} from '@/apis/shipment';
import { Flex, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';
import { DrawerState } from '@/pages/Shipments/ShipmentDrawer';
import ArrowBack from '@/assets/images/ArrowBack';
import { InboundIcon, OutboundIcon, StarIcon } from '@/assets/icons/icons';
import SettingsAgentDrawerForm, {
  AgentDrawerFormRef,
} from '@/pages/Settings/AiAgents/SettingsAgentDrawerForm';
import { AgentRow } from '@/pages/Settings/AiAgents/types';
import styles from './Transcripts.module.css';

interface TranscriptActionMetadata {
  id?: string;
  notification_type?: string;
  ai_events?: Array<{ id?: string }>;
}

interface TranscriptsProps {
  toggleDrawer: (
    action: 'open' | 'close',
    type?: string,
    shipmentId?: string
  ) => void;
  drawerState: DrawerState;
  shipmentId: string;
}

/**
 * Transcripts component that conditionally renders email or call transcript components
 *
 * @param toggleDrawer - Function to control drawer state
 * @param drawerState - Current drawer state with name and alert data
 * @param shipmentId - ID of the shipment
 */
export const Transcripts: React.FC<TranscriptsProps> = ({
  toggleDrawer,
  drawerState,
  shipmentId,
}) => {
  const { name, alert } = drawerState;
  // The shared drawer type omits AI notification metadata supplied by its callers.
  const actionMetadata = alert as TranscriptActionMetadata | null | undefined;
  const { showSpin, hideSpin } = useSpin();
  const [alertId, setAlertId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [agentPanel, setAgentPanel] = useState<{
    open: boolean;
    agentId: string | null;
    agentType: string | null;
    readOnly: boolean;
  }>({ open: false, agentId: null, agentType: null, readOnly: true });
  const [testLoading, setTestLoading] = useState(false);
  const formRef = useRef<AgentDrawerFormRef>(null);

  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const onClose = (close?: boolean): void => {
    if (close) {
      toggleDrawer('close');
    } else {
      toggleDrawer('open', 'Detail', shipmentId);
    }
  };

  const buildResolveModel = (
    isResolved: boolean,
    targetAlertId: string
  ): ResolveAlertCommentsModel => ({
    is_resolved: isResolved,
    pk_list: [targetAlertId],
    completion_context: name === 'callTranscripts' ? 'ai_call' : 'ai_email',
  });

  const getOneTimeId = (): string | undefined =>
    actionMetadata?.ai_events?.[0]?.id ?? actionMetadata?.id;

  // The panels list this callback as a dependency of the effect that loads their
  // transcript, so it must keep one identity. A new one on every render made them
  // reload again each time the spinner (which they show while loading) re-rendered
  // this component: an endless reload that flickered the screen.
  const unauthorizeRef = useRef(unauthorize);
  unauthorizeRef.current = unauthorize;
  const handleTranscriptError = useCallback((error: unknown): void => {
    const status =
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof error.status === 'number'
        ? error.status
        : undefined;
    unauthorizeRef.current(status === undefined ? undefined : { status });
  }, []);

  const runAlertAction = async (isResolve: boolean) => {
    if (!token) return;
    showSpin();
    try {
      const isOneTime =
        actionMetadata?.notification_type === 'one_time_notification';
      if (isOneTime) {
        const id = getOneTimeId();
        if (!id) {
          message.error(
            'Unable to find the AI notification. Refresh and try again.'
          );
          return;
        }
        const res = await (isResolve
          ? oneTimeResolve(token, id)
          : oneTimeDismiss(token, id));

        const data = res?.data?.data || null;
        if (data?.is_success) {
          setRefreshKey((prev) => prev + 1);
          message.success(
            data?.message ||
              (isResolve ? 'Resolved successfully' : 'Dismissed successfully')
          );
        } else {
          message.error(
            data?.message || (isResolve ? 'Resolve failed' : 'Dismiss failed')
          );
        }
      } else {
        if (!alertId) {
          message.error(
            'Wait for the transcript to load before closing this alert.'
          );
          return;
        }
        const model = buildResolveModel(isResolve, alertId);
        const res = await ResolveAlertComments(token, model);
        const data = res?.data || null;

        if (data?.data?.is_success) {
          setRefreshKey((prev) => prev + 1);
          message.success(
            data?.data?.message ||
              (isResolve ? 'Resolved successfully' : 'Dismissed successfully')
          );
          toggleDrawer('open', 'Detail', shipmentId);
        } else {
          message.error(
            data?.data?.message ||
              (isResolve ? 'Resolve failed' : 'Dismiss failed')
          );
        }
      }
    } catch {
      unauthorize();
    } finally {
      hideSpin();
    }
  };

  const handleDismiss = (): void => {
    runAlertAction(false).catch((error: unknown) => {
      console.error('Failed to dismiss transcript alert', error);
    });
  };

  const handleResolve = (): void => {
    runAlertAction(true).catch((error: unknown) => {
      console.error('Failed to resolve transcript alert', error);
    });
  };

  const openAgent = (agentId: string, readOnly: boolean): void => {
    setAgentPanel({ open: true, agentId, agentType: null, readOnly });
  };

  const closeAgent = (): void => {
    setAgentPanel({
      open: false,
      agentId: null,
      agentType: null,
      readOnly: true,
    });
  };

  if (!token || !alert) return null;

  if (agentPanel.open && agentPanel.agentId) {
    const isInbound = agentPanel.agentType?.toLowerCase() === 'inbound';
    return (
      <div className={styles.agentPanel}>
        <Flex
          align='center'
          justify='space-between'
          style={{ marginBottom: 8 }}
        >
          <ArrowBack
            fill='var(--text-primary)'
            style={{ width: 16, height: 16, cursor: 'pointer' }}
            onClick={closeAgent}
          />
          <CloseOutlined
            style={{ width: 13, height: 13, paddingTop: 5, cursor: 'pointer' }}
            onClick={() => onClose(true)}
          />
        </Flex>
        <div
          className={`agentTypePill ${styles.agentTypePill} ${agentPanel.agentType ? '' : styles.hiddenAgentType}`}
        >
          {isInbound ? (
            <InboundIcon color='var(--text-secondary)' />
          ) : (
            <OutboundIcon color='var(--text-secondary)' />
          )}
          <span className='agentTypePill__text'>
            {isInbound ? 'Inbound' : 'Outbound'}
          </span>
        </div>
        <Flex align='center' justify='space-between'>
          <div className='ai-header-title'>Edit AI agent</div>
          {!agentPanel.readOnly && (
            <button
              type='button'
              className='agentFormTestBtn'
              disabled={testLoading}
              onClick={() => formRef.current?.triggerTest()}
            >
              <StarIcon color='var(--text-primary)' />
              {testLoading ? 'Testing...' : 'Test agent'}
            </button>
          )}
        </Flex>
        <div className={styles.agentForm}>
          <SettingsAgentDrawerForm
            ref={formRef}
            agentData={{ id: agentPanel.agentId } as AgentRow}
            drawerOpen
            onClose={() => onClose(true)}
            readOnly={agentPanel.readOnly}
            hideAgentHeader
            onAgentTypeLoaded={(type) =>
              setAgentPanel((prev) => ({ ...prev, agentType: type }))
            }
            onTestLoadingChange={setTestLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {name === 'emailTranscripts' && (
        <AiEmailExchange
          key={refreshKey}
          setAlertId={setAlertId}
          onClose={onClose}
          unauthorize={handleTranscriptError}
          token={token}
          alert={alert}
          onDismiss={handleDismiss}
          onResolve={handleResolve}
          onOpenAgent={openAgent}
          shipmentId={shipmentId}
        />
      )}

      {name === 'callTranscripts' && (
        <AiCallTranscript
          key={refreshKey}
          setAlertId={setAlertId}
          onClose={onClose}
          unauthorize={handleTranscriptError}
          token={token}
          alert={alert}
          onDismiss={handleDismiss}
          onResolve={handleResolve}
          onOpenAgent={openAgent}
          shipmentId={shipmentId}
        />
      )}
    </>
  );
};
